// src/utils/envoiBE.ts — SCE Issues
// « Envoyer au BE » (fiche problème, 20/10) : ce qui part, et sous quelle
// forme. Maquette validée : https://claude.ai/artifact/D9KRDkXqNmEYL9aQDp7Jwd
//
// Le coordinateur transmet un problème au bureau d'études de son métier (ou à
// quelqu'un d'autre) : une carte Teams et/ou un mail, avec la fiche résumée
// et les commentaires utiles. Les photos ne partent plus (Guillaume, 22/10) :
// le message les annonce et renvoie à la fiche, où on les voit. L'appli n'envoie rien elle-même (pas
// de serveur, pas de droit d'envoi) : elle écrit une ligne dans SCE_Envois,
// le flow 6 l'envoie (docs/flows/flow-6-envoi-be.md).
//
// ─── Le choix des commentaires (règles fixes, sans IA) ───
//
// Toujours : la description et la qualification (dans les faits du message),
// le dernier commentaire du coordinateur. En priorité : une mesure ou une
// référence (mm, bar, N° de plan, NVS, EDE…), une photo, le coordinateur, le
// déclarant, les deux plus récents. Écartés : moins de 25 caractères sans
// chiffre (« ok », « je repasse demain »), les textes automatiques. Au plus
// 6, dans l'ordre chronologique. Chaque ligne dit POURQUOI elle est gardée ou
// écartée : la vue « Voir et ajuster » l'affiche, le coordinateur corrige.
//
// Tout le message (objet, mail en HTML, carte Teams en JSON) est construit
// ICI : le flow n'a qu'à recopier. Pur : ni React, ni réseau.

import { A_QUALIFIER, type Commentaire, type Issue, type Metier } from '@/types/issue.types'
import { METIERS_LABELS_COURT } from '@/config/metiers.config'
import { memePersonne } from './fnct'
import { estTexteAutomatique } from './rattachementPhotos'
import {
  carteAdaptative,
  echapper,
  enHtml,
  enteteCarte,
  enveloppeMail,
  FILET_MAIL,
  GRIS_MAIL,
  lignesFaits,
  sectionMail,
  texteCarte,
  TEXTE_MAIL,
  tronquer,
} from './gabaritMessage'

export { LOGO_MAIL, tronquer } from './gabaritMessage'

export const MAX_COMMENTAIRES = 6
/** En dessous, un commentaire sans chiffre n'apprend rien au BE. */
export const LONGUEUR_COURT = 25
/** Longueur d'un commentaire dans le message (la fiche garde le texte entier). */
export const LONGUEUR_EXTRAIT = 300
const LONGUEUR_DESCRIPTION = 600

// ─── Choix des commentaires ───

export type RaisonChoix =
  | 'mesure'
  | 'photo'
  | 'coordinateur'
  | 'declarant'
  | 'recent'
  | 'commentaire'
  | 'court'
  | 'au-dela'

/** Ce que dit l'étiquette de la vue « Voir et ajuster ». */
export const LIBELLES_RAISON: Readonly<Record<RaisonChoix, string>> = {
  mesure: 'Mesure ou référence',
  photo: 'Avec photo',
  coordinateur: 'Coordinateur',
  declarant: 'Déclarant',
  recent: 'Récent',
  commentaire: 'Commentaire',
  court: 'Trop court',
  'au-dela': `Au-delà de ${MAX_COMMENTAIRES}`,
}

/** Poids de chaque raison, et ordre dans lequel l'étiquette la cite. */
const POIDS: ReadonlyArray<readonly [RaisonChoix, number]> = [
  ['mesure', 3],
  ['photo', 2],
  ['coordinateur', 2],
  ['declarant', 1],
  ['recent', 1],
]

export interface CommentaireCandidat {
  commentaire: Commentaire
  /** Coché d'office. */
  garde: boolean
  raison: RaisonChoix
}

export interface ContexteChoix {
  /** Noms des coordinateurs et suppléants du métier. */
  coordinateurs: readonly string[]
  /** Nom du déclarant. */
  declarant: string
  /** Identifiants des commentaires qui portent une photo. */
  avecPhoto: ReadonlySet<string>
}

const UNITE = /\d+(?:[.,]\d+)?\s?(?:mm|cm|µm|um|m|bars?|mbar|°C|°|l\/h|L\/h|ml|l|L|kg|g|N\.?m|kW|W|V|mA|A|Hz|rpm|tr\/min|%)(?![\p{L}\d])/u
const REFERENCE = /\b(?:plans?|NVS\s?\d|EDE|rev\.?\s?\d|indice|OF[-\s]?\d{4}|PN\s?\d{4}|CVE[-\s]?\d{4})|\b\d{4}-(?:SC|SD|H|S|B)\b/i

/** Une mesure (« 8 mm », « 100 bar ») ou une référence (plan, NVS, EDE, OF, N° de série…). */
export function porteMesureOuReference(texte: string): boolean {
  return UNITE.test(texte) || REFERENCE.test(texte)
}

/** « ok », « merci », « je repasse demain » : court, et sans aucun chiffre. */
export function estCourt(texte: string): boolean {
  const t = texte.trim()
  return t.length < LONGUEUR_COURT && !/\d/.test(t)
}

/**
 * Les commentaires du problème, chacun avec son choix et sa raison, dans
 * l'ordre chronologique. Les événements (création, qualification, statut,
 * modifications) et les textes automatiques (« Fichier ajouté ») n'y sont pas :
 * les faits du message disent déjà la qualification.
 */
export function choisirCommentaires(
  commentaires: readonly Commentaire[],
  contexte: ContexteChoix,
  max = MAX_COMMENTAIRES,
): CommentaireCandidat[] {
  const eligibles = commentaires.filter(
    (c) => c.type === 'Commentaire' && c.contenu.trim() !== '' && !estTexteAutomatique(c.contenu),
  )
  const parCoordinateur = (c: Commentaire): boolean => contexte.coordinateurs.some((n) => memePersonne(n, c.auteur))
  // Un commentaire court mais avec photo dit « voir la photo » : il reste.
  const court = (c: Commentaire): boolean => estCourt(c.contenu) && !contexte.avecPhoto.has(c.id)
  const utiles = eligibles.filter((c) => !court(c))
  const recents = new Set(utiles.slice(-2).map((c) => c.id))
  const dernierDuCoordinateur = [...utiles].reverse().find(parCoordinateur)?.id

  const notes = utiles.map((c, rang) => {
    const raisons: RaisonChoix[] = []
    if (porteMesureOuReference(c.contenu)) raisons.push('mesure')
    if (contexte.avecPhoto.has(c.id)) raisons.push('photo')
    if (parCoordinateur(c)) raisons.push('coordinateur')
    if (memePersonne(contexte.declarant, c.auteur)) raisons.push('declarant')
    if (recents.has(c.id)) raisons.push('recent')
    const score = POIDS.reduce((s, [r, p]) => (raisons.includes(r) ? s + p : s), 0)
    return { c, rang, raisons, score, force: c.id === dernierDuCoordinateur }
  })

  const gardes = new Set(
    (notes.length <= max
      ? notes
      : [...notes].sort((a, b) => Number(b.force) - Number(a.force) || b.score - a.score || b.rang - a.rang).slice(0, max)
    ).map((n) => n.c.id),
  )
  const parId = new Map(notes.map((n) => [n.c.id, n]))

  return eligibles.map((c) => {
    const n = parId.get(c.id)
    if (!n) return { commentaire: c, garde: false, raison: 'court' }
    if (!gardes.has(c.id)) return { commentaire: c, garde: false, raison: 'au-dela' }
    return { commentaire: c, garde: true, raison: n.raisons[0] ?? 'commentaire' }
  })
}

// ─── Destinataires ───
//
// Pas de choix du canal dans la fenêtre (Guillaume, 21/10) : comme toutes les
// notifications, chacun reçoit par mail et / ou Teams selon SES réglages
// « Mes notifications » (CanauxNotification) — le flow 6 les lit. Une personne
// qui a tout coupé (« Aucun ») reçoit quand même le mail : un envoi voulu doit
// arriver.

export interface Destinataire {
  nom: string
  upn: string
}

const memeAdresse = (a: string, b: string): boolean => a.trim().toLowerCase() === b.trim().toLowerCase()

// ─── Les personnes à qui j'envoie le plus (22/10) ───
//
// Proposées dans « + Quelqu'un d'autre », champ vide. Bloc `envoisBE` du JSON
// Prefs de SCE_UserPrefs (même ligne que le reste, ecrireBlocPrefs) : suivies
// d'un appareil à l'autre. Seules les personnes ajoutées à la main comptent :
// le BE du métier est déjà là d'office.

export const CLE_BLOC_ENVOIS = 'envoisBE'
/** Au-delà, les moins fréquentes (puis les plus anciennes) sont oubliées. */
export const MAX_FREQUENTS = 30

export interface DestinataireFrequent extends Destinataire {
  /** Nombre d'envois. */
  n: number
  /** Date ISO du dernier envoi. */
  le: string
}

/** Lecture tolérante du bloc : entrée sans adresse ou mal formée écartée. */
export function lireFrequents(bloc: unknown): DestinataireFrequent[] {
  const liste = (bloc as { personnes?: unknown } | null | undefined)?.personnes
  if (!Array.isArray(liste)) return []
  return liste.flatMap((x: unknown) => {
    const p = x as Partial<DestinataireFrequent> | null
    if (!p || typeof p.upn !== 'string' || !p.upn.includes('@')) return []
    return [{ nom: typeof p.nom === 'string' && p.nom ? p.nom : p.upn, upn: p.upn.toLowerCase(), n: typeof p.n === 'number' && p.n > 0 ? p.n : 1, le: typeof p.le === 'string' ? p.le : '' }]
  })
}

const parFrequence = (a: DestinataireFrequent, b: DestinataireFrequent): number => b.n - a.n || b.le.localeCompare(a.le)

/** Le bloc après un envoi à `personnes` : +1 chacune, nom mis à jour, 30 au plus. */
export function noterEnvoi(bloc: unknown, personnes: readonly Destinataire[], quand: Date): { version: 1; personnes: DestinataireFrequent[] } {
  const liste = lireFrequents(bloc)
  const le = quand.toISOString()
  for (const p of personnes) {
    const deja = liste.find((x) => memeAdresse(x.upn, p.upn))
    if (deja) {
      deja.n += 1
      deja.le = le
      if (p.nom) deja.nom = p.nom
    } else liste.push({ nom: p.nom || p.upn, upn: p.upn.toLowerCase(), n: 1, le })
  }
  return { version: 1, personnes: liste.sort(parFrequence).slice(0, MAX_FREQUENTS) }
}

/** Les plus fréquentes, sauf celles déjà dans « À ». */
export function habituesEnvoi(frequents: readonly DestinataireFrequent[], exclus: readonly Destinataire[], max = 6): Destinataire[] {
  return [...frequents]
    .sort(parFrequence)
    .filter((f) => !exclus.some((d) => memeAdresse(d.upn, f.upn)))
    .slice(0, max)
    .map(({ nom, upn }) => ({ nom, upn }))
}

/**
 * À qui part le message, en clair : « au BE MECA », « au BE MECA et à Jean
 * Dupont », « à Jean Dupont », « à 3 personnes ». Sert au bouton, à l'objet du
 * mail et à l'activité.
 */
export function libelleDestination(
  metier: Metier,
  destinataires: readonly Destinataire[],
  be: readonly Destinataire[],
): string {
  const nomBE = `BE ${METIERS_LABELS_COURT[metier]}`
  const beEntier = be.length > 0 && be.every((b) => destinataires.some((d) => memeAdresse(d.upn, b.upn)))
  const autres = destinataires.filter((d) => !be.some((b) => memeAdresse(b.upn, d.upn)))
  const lesAutres = autres.length === 1 ? (autres[0]?.nom ?? '') : `${autres.length} autres personnes`
  if (beEntier) return autres.length === 0 ? `au ${nomBE}` : `au ${nomBE} et à ${lesAutres}`
  if (destinataires.length === 1) return `à ${destinataires[0]?.nom ?? ''}`
  return `à ${destinataires.length} personnes`
}

// ─── Le message ───

export interface ContenuEnvoi {
  issue: Pick<
    Issue,
    | 'id'
    | 'titre'
    | 'description'
    | 'metier'
    | 'famille'
    | 'criticite'
    | 'statut'
    | 'numeroSerie'
    | 'pn'
    | 'numeroPlan'
    | 'numeroEDE'
    | 'revEDE'
    | 'pageEDE'
  >
  equipement?: { modele?: string; chefProjet?: string }
  /** Qui transmet (nom du compte réel). */
  expediteur: string
  note: string
  /** Les commentaires retenus, dans l'ordre chronologique. */
  commentaires: readonly Commentaire[]
  /** Commentaires laissés de côté (« et 3 autres sur la fiche »). */
  autres: number
  /** Photos du problème : annoncées, vues sur la fiche (elles ne partent pas). */
  nbPhotos: number
  /** Adresse de la fiche dans l'appli. */
  lien: string
  /** « au BE MECA » (libelleDestination). */
  destination: string
}

function pluriel(n: number, mot: string): string {
  return `${n} ${mot}${n > 1 ? 's' : ''}`
}

/** « 1 autre commentaire », « 3 autres commentaires ». */
function autresCommentaires(n: number): string {
  return n > 1 ? `${n} autres commentaires` : `${n} autre commentaire`
}

/** « 14/10 » */
function jourMois(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const p = (n: number): string => String(n).padStart(2, '0')
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}`
}

/** Les lignes « Équipement · Projet · Criticité… » du message, vides écartées. */
export function faitsDuMessage(c: ContenuEnvoi): Array<[string, string]> {
  const i = c.issue
  const qualifie = i.criticite !== A_QUALIFIER
  const ede = i.numeroEDE
    ? [`${i.numeroEDE}${i.revEDE ? ` Rev ${i.revEDE}` : ''}`, i.pageEDE ? `p. ${i.pageEDE}` : ''].filter(Boolean).join(' · ')
    : ''
  const faits: Array<[string, string]> = [
    ['Équipement', [i.numeroSerie, c.equipement?.modele].filter(Boolean).join(' · ')],
    ['Projet', [i.pn, c.equipement?.chefProjet].filter(Boolean).join(' · ')],
    ['Criticité', qualifie ? [i.criticite, i.famille !== A_QUALIFIER ? i.famille : ''].filter(Boolean).join(' · ') : 'À qualifier'],
    ['Statut', i.statut],
    ['N° de plan', (i.numeroPlan ?? '').split('\n').map((l) => l.trim()).filter(Boolean).join(', ')],
    ['EDE', ede],
  ]
  return faits.filter(([, v]) => v.trim() !== '')
}

/** Objet du mail (colonne texte : 255 caractères au plus). */
export function objetMail(c: ContenuEnvoi): string {
  return tronquer(`[SCE Issues] ${c.issue.id} transmis ${c.destination} · ${c.issue.titre}`, 250)
}

/**
 * Le corps du mail, en HTML autonome (styles en ligne : Outlook ignore les
 * feuilles de style), même forme que l'alerte des coordinateurs (flow 1) :
 * carte blanche de 600 px sur fond gris, bandeau jaune avec le logo (comme les
 * audits 5S), ID puis titre, tableau des faits à filets, sections en petites
 * capitales, bouton jaune, pied gris (22/10 : la 1re version était trop aérée).
 * Les couleurs sont écrites en clair — c'est un mail, pas une page de l'appli.
 * Mise en page en tableaux : Outlook ignore une bonne part du CSS des <div>.
 */
export function corpsMail(c: ContenuEnvoi): string {
  const faits = lignesFaits(faitsDuMessage(c), (l, v) => (l === 'Criticité' && v.startsWith('Critique') ? '#E60046' : '#000000'))

  const note = c.note.trim()
    ? `<div style="margin-top:10px;border-left:3px solid #FFED00;background-color:#F5F5F5;padding:8px 12px;font-size:15px;line-height:21px;color:#000000">${enHtml(c.note.trim())}</div>`
    : ''

  const commentaires = c.commentaires
    .map(
      (k, i) =>
        `<div style="padding:6px 0;${i > 0 ? `border-top:1px solid ${FILET_MAIL};` : ''}font-size:14px;line-height:20px;color:${TEXTE_MAIL}">` +
        `<b style="color:#000000">${echapper(`${jourMois(k.date)} · ${k.auteur}`)}</b> — ${enHtml(tronquer(k.contenu, LONGUEUR_EXTRAIT))}</div>`,
    )
    .join('')
  const autres = c.autres > 0
    ? `<div style="padding-top:4px;font-size:13px;color:${GRIS_MAIL}">et ${autresCommentaires(c.autres)} sur la fiche</div>`
    : ''

  const photos = c.nbPhotos > 0
    ? `<div style="margin-top:8px;font-size:13px;color:${GRIS_MAIL}">${pluriel(c.nbPhotos, 'photo')} sur la fiche.</div>`
    : ''

  return enveloppeMail({
    // Qui transmet, et son mot : ce que le BE doit lire d'abord.
    intro: `<b style="color:#000000">${echapper(c.expediteur)}</b> vous transmet ce problème.${note}`,
    surtitre: c.issue.id,
    titre: c.issue.titre,
    faits,
    sections:
      (c.issue.description.trim()
        ? sectionMail('Description', `<div style="font-size:14px;line-height:20px;color:${TEXTE_MAIL}">${enHtml(tronquer(c.issue.description, LONGUEUR_DESCRIPTION))}</div>`)
        : '') + (commentaires ? sectionMail('Ce qui s’est dit', commentaires + autres) : ''),
    lien: c.lien,
    libelleBouton: 'Ouvrir la fiche dans SCE Issues',
    apresBouton: photos,
    // « Répondre à » mène au coordinateur (flow 6), pas à une boîte muette.
    pied: `Répondre à ce mail écrit directement à ${echapper(c.expediteur)}.`,
  })
}

/**
 * La carte Teams (Adaptive Card 1.4), en objet : la colonne CarteTeams en
 * garde le JSON, le flow la publie telle quelle. En tête, le logo et « SCE Issues »,
 * comme la carte du flow 1 (22/10) : le logo est public, Teams l'affiche. Pas de
 * photo du problème : Teams ne sait pas afficher une image SharePoint (session
 * requise) et une carte ne dépasse pas 28 Ko ; on les voit dans l'appli, via
 * « Ouvrir dans SCE Issues » (21/10). Pas de
 * Markdown non plus : chaque texte saisi est un bloc à part, rien à échapper.
 */
export function carteTeams(c: ContenuEnvoi): Record<string, unknown> {
  const texte = texteCarte
  const titre = (t: string): Record<string, unknown> => texte(t, { weight: 'Bolder', spacing: 'Medium' })
  const corps: Record<string, unknown>[] = [
    enteteCarte(),
    texte(`${c.expediteur} vous transmet un problème`, { isSubtle: true, size: 'Small', spacing: 'Medium' }),
    texte(`${c.issue.id} · ${c.issue.titre}`, { weight: 'Bolder', size: 'Medium', spacing: 'Small' }),
    { type: 'FactSet', facts: faitsDuMessage(c).map(([title, value]) => ({ title, value })) },
  ]
  if (c.note.trim()) {
    corps.push({ type: 'Container', style: 'emphasis', spacing: 'Medium', items: [texte(c.note.trim())] })
  }
  if (c.issue.description.trim()) {
    corps.push(titre('Description'), texte(tronquer(c.issue.description, LONGUEUR_DESCRIPTION), { spacing: 'Small' }))
  }
  if (c.commentaires.length > 0) {
    corps.push(titre('Ce qui s’est dit'))
    for (const k of c.commentaires) {
      corps.push({
        type: 'Container',
        spacing: 'Small',
        items: [
          texte(`${jourMois(k.date)} · ${k.auteur}`, { weight: 'Bolder', size: 'Small' }),
          texte(tronquer(k.contenu, LONGUEUR_EXTRAIT), { spacing: 'None' }),
        ],
      })
    }
    if (c.autres > 0) corps.push(texte(`et ${autresCommentaires(c.autres)} sur la fiche`, { isSubtle: true, size: 'Small' }))
  }
  if (c.nbPhotos > 0) {
    corps.push(texte(`${pluriel(c.nbPhotos, 'photo')} sur la fiche`, { isSubtle: true, size: 'Small', spacing: 'Medium' }))
  }
  return carteAdaptative(corps, c.lien)
}

// ─── La ligne de SCE_Envois ───

export interface EnvoiADeposer {
  issueId: string
  destinataires: readonly Destinataire[]
  contenu: ContenuEnvoi
  /** Adresse de qui transmet : toujours le « Répondre à » du mail. */
  expediteurUpn: string
  /** Le coordinateur veut-il aussi une copie du mail (case de la fenêtre, cochée d'office, 23/10) ? */
  copieExpediteur: boolean
}

/**
 * Les colonnes de la ligne (noms internes = noms créés, sans espace ni accent).
 * Le flow 6 se déclenche à sa création et n'a qu'à recopier ; le canal de
 * chaque destinataire, il le lit dans ses réglages (pas de colonne Canaux), et
 * le Cc du mail, dans `CopieExpediteur` (`Oui` / `Non`, vide = copie).
 */
export function champsEnvoi(e: EnvoiADeposer): Record<string, string> {
  return {
    Title: e.issueId,
    Destinataires: e.destinataires.map((d) => d.upn.trim()).join(';'),
    Objet: objetMail(e.contenu),
    CorpsHtml: corpsMail(e.contenu),
    CarteTeams: JSON.stringify(carteTeams(e.contenu)),
    Note: e.contenu.note.trim(),
    Expediteur: e.expediteurUpn,
    CopieExpediteur: e.copieExpediteur ? 'Oui' : 'Non',
  }
}

// ─── L'événement de l'activité ───

/**
 * « Envoyé au BE MECA : Jean Dupont, Marie Martin », puis
 * « 3 commentaires », puis la note entre guillemets. Événement
 * `Modification`, comme la NC-S : aucune valeur de choix à ajouter.
 */
export function texteEvenementEnvoi(
  destination: string,
  destinataires: readonly Destinataire[],
  nbCommentaires: number,
  note: string,
): string {
  const lignes = [
    `Envoyé ${destination} : ${destinataires.map((d) => d.nom || d.upn).join(', ')}`,
    pluriel(nbCommentaires, 'commentaire'),
  ]
  if (note.trim()) lignes.push(`« ${note.trim()} »`)
  return lignes.join('\n')
}

/** Les envois du 20/10 portaient encore le canal choisi : « (Teams et mail) ». */
const LIGNE_ENVOI = /^Envoyé ((?:au BE|à) .+?)(?: \((?:Teams et mail|Teams|mail)\))? : /

/** Relit un événement d'envoi ; null pour tout autre événement. */
export function lireEvenementEnvoi(contenu: string): { destination: string } | null {
  const m = LIGNE_ENVOI.exec(contenu)
  return m ? { destination: m[1] ?? '' } : null
}

/**
 * Ce que l'accueil montre sous « Vous avez envoyé P2026-0187 » (22/10) : à
 * qui, sans répéter les noms quand la destination les dit déjà, puis le mot du
 * coordinateur — « Au BE MECA · « Plan à reprendre » », « À Jean Dupont »,
 * « À Anne Leroy, Marc Petit ». Null pour tout autre événement.
 */
export function resumeEvenementEnvoi(contenu: string): string | null {
  const lu = lireEvenementEnvoi(contenu)
  if (!lu) return null
  const lignes = contenu.split('\n').map((l) => l.trim())
  const noms = (lignes[0] ?? '').split(' : ').slice(1).join(' : ').trim()
  const destination = /^à \d+ personnes$/.test(lu.destination) && noms ? `à ${noms}` : lu.destination
  const note = lignes.find((l) => l.startsWith('«'))
  const qui = destination.charAt(0).toUpperCase() + destination.slice(1)
  return note ? `${qui} · ${note}` : qui
}

/** Le dernier envoi du problème (infobulle de l'icône), ou null. */
export function dernierEnvoi(
  commentaires: readonly Commentaire[],
): { date: string; auteur: string; destination: string } | null {
  for (let i = commentaires.length - 1; i >= 0; i--) {
    const c = commentaires[i]
    if (!c || c.type !== 'Modification') continue
    const lu = lireEvenementEnvoi(c.contenu)
    if (lu) return { date: c.date, auteur: c.auteur, destination: lu.destination }
  }
  return null
}
